import {useAuth0} from '@auth0/auth0-react';
import {Patient} from 'gabi-api-ts/v2/patient/query/patient_query';
import {JSX, useMemo, useState} from 'react';
import {Beforeunload} from 'react-beforeunload';
import {useTranslation} from 'react-i18next';
import ReactRouterPrompt from 'react-router-prompt';
import Styled from 'styled-components';

import PatientCreationCaregiver from '@/components/business/analytics/patient/patient-creation/patient-creation-caregiver';
import {PatientCreationChild} from '@/components/business/analytics/patient/patient-creation/patient-creation-child';
import {PatientCreationEventConfiguration} from '@/components/business/analytics/patient/patient-creation/patient-creation-event-configuration';
import PatientCreationGroups from '@/components/business/analytics/patient/patient-creation/patient-creation-groups';
import PatientCreationHealthCondition from '@/components/business/analytics/patient/patient-creation/patient-creation-health-condition';
import PatientCreationOnboarding from '@/components/business/analytics/patient/patient-creation/patient-creation-onboarding';
import PatientCreationPrescriber from '@/components/business/analytics/patient/patient-creation/patient-creation-prescriber';
import {Button} from '@/components/buttons/button';
import ErrorBoundary from '@/components/errors/error-boundary';
import {Modal} from '@/components/layout/modal';
import {Page} from '@/decorators/page';
import {colorPalette} from '@/themes/darkmode';
import {PatientType} from 'gabi-api-ts/v2/patient/common/patient_common';

function wrapWithPage(Component: JSX.ElementType) {
    return Page({
        name: 'New patient',
        pagePath: () => ([
            {
                route: 'patients',
                name: 'Patients',
            },
        ])
    })(Component);
}

type PatientCreationPageProps = {
    className: string;
};

function PatientCreationPageUnstyled(props: PatientCreationPageProps) {
    const { t, i18n } = useTranslation();
    const auth0 = useAuth0();
    const me = auth0.user;

    const [activeStep, setActiveState] = useState<number>(0);
    const [patientId, setPatientId] = useState<string | null>(null);
    const [patient, setPatient] = useState<Patient | null>(null);
    const [notSavedDataChild, setNotSavedDataChild] = useState<boolean>(false);

    const handlePatientCreated = (newPatient: Patient) => {
        setPatient(newPatient);
        setPatientId(newPatient.patientId?.id ?? null);
        handleStepSaved();
    };

    const handleStepSaved = () => {
        let nextStep = activeStep+1;

        // Skip step 2 event config if parent
        // TODO use a better mechanism to skip steps, inline ifs are a pain to maintain
        if (patient?.patientType == PatientType.Adult && nextStep == 2) {
            nextStep++;
        }

        setActiveState(nextStep);
        setNotSavedDataChild(0 < nextStep && nextStep < 4);
    };

    const steps = useMemo(() => {
        return [
            t('pages.patientCreation.steps.childInformation'),
            t('pages.patientCreation.steps.healthCondition'),
            t('pages.patientCreation.steps.eventConfiguration'),
            t('pages.patientCreation.steps.caregiverInformation'),
            t('pages.patientCreation.steps.prescriber'),
            t('pages.patientCreation.steps.accessGroups'),
            t('pages.patientCreation.steps.onboarding'),
        ];
    }, [i18n.language]);

    return (
        <div className={`patient-creation-page ${props.className ?? ''}`}>
            <ErrorBoundary>
                <ReactRouterPrompt when={notSavedDataChild}>
                    {({ isActive, onConfirm, onCancel }) =>
                        isActive && (
                            <Modal title={t('pages.patientCreation.notSavedDataChild.title')} onClosed={onCancel}>
                                <p>{t('pages.patientCreation.notSavedDataChild.text')}</p>
                                <Button displayIcon={false} className="button-delete" type="submit" onClick={onConfirm}>
                                    {t('pages.patientCreation.notSavedDataChild.confirm')}
                                </Button>
                                <Button displayIcon={false} className="button-cancel" type="button" onClick={onCancel}>
                                    {t('pages.patientCreation.notSavedDataChild.cancel')}
                                </Button>
                            </Modal>
                        )
                    }
                </ReactRouterPrompt>

                <Beforeunload onBeforeunload={() => 'You’ll lose your data!'} />

                <div className="sections-wrapper">
                    <ul className="sections-menu wizardSteps">
                        {steps.map((step, i) => (
                            <li key={i} className={(activeStep === i) ? 'activeStep': (i < activeStep) ? 'completedStep': ''}>{step}<span>{i+1}</span></li>
                        ))}
                    </ul>

                    <div className="sections-content">
                        {activeStep === 0 &&
                            <PatientCreationChild onCreated={handlePatientCreated} onChanged={setNotSavedDataChild} />
                        }
                        {activeStep === 1 && patientId &&
                            <PatientCreationHealthCondition patientId={patientId} onSaved={handleStepSaved} />
                        }
                        {activeStep === 2 && patientId &&
                            <PatientCreationEventConfiguration patientId={patientId} onSaved={handleStepSaved} />
                        }
                        {activeStep === 3 && patientId &&
                            <PatientCreationCaregiver patientId={patientId} onSaved={handleStepSaved} />
                        }
                        {activeStep === 4 && patientId &&
                            <PatientCreationPrescriber patientId={patientId} me={me} onSaved={handleStepSaved} />
                        }
                        {activeStep === 5 && patientId &&
                            <PatientCreationGroups patientId={patientId} onSaved={handleStepSaved} />
                        }
                        {activeStep === 6 && patientId &&
                            <PatientCreationOnboarding patientId={patientId} onSaved={handleStepSaved} />
                        }
                    </div>
                </div>
            </ErrorBoundary>
        </div>
    );
}

//language=SCSS
const PatientCreationPage = Styled(wrapWithPage(PatientCreationPageUnstyled))`
& {
    min-height: 500px;
    width: 90%;
    height: calc(100% - 80px);
    margin: 70px auto 0 auto;
    
    .wizardSteps {
        margin:0;
        padding:0;
        width:300px;
        border-right:1px solid ${colorPalette.clearColor};
        
        li {
            list-style-type: none;
            position: relative;
            padding-right: 40px;
            padding-top: 4px;
            text-align: right;
            height: 60px;
            span {
                display:block;
                padding:5px;
                color:${colorPalette.frontColor};
                border-radius:50px;
                width:30px;
                height:30px;
                text-align:center;
                background-color:${colorPalette.secondBackground};
                position:absolute;
                right:-15px;
                top:0;
            }
            &.activeStep {
                color:${colorPalette.activeColor};
                span {
                  background-color: ${colorPalette.activeColor};
                }
            }
            &.completedStep span {
                background-color:${colorPalette.success};
            }
        }
    }
    
    .sections-content {
        .info-message {
            margin-bottom: 20px;
        }
    }
    
    h3 {
        font-size: 16px;
        margin-bottom: 10px;
    }
    
    .no-group-block {
        width:800px;
    }
}
`;

export {PatientCreationPage};
